import { z } from 'zod';
import { BR_STATES, cleanCep } from './address.ts';
import { countryCode, uuid } from './schemas.ts';
import { isValidCnpj, isValidCpf, normalizeTaxId } from './tax-id.ts';

export { isValidCnpj, isValidCpf, normalizeTaxId };

/**
 * Cadastro corporativo de parceiros (ADRs 0013 e 0014): validações, entradas da API e o cálculo
 * da situação e da aptidão, usados pela API, pelas telas e, depois, por cotações e pagamentos.
 */

export const PARTNER_PERSON_TYPES = ['individual', 'company'] as const;
export type PartnerPersonType = (typeof PARTNER_PERSON_TYPES)[number];

export const PARTNER_TAX_ID_TYPES = ['cpf', 'cnpj', 'foreign', 'none'] as const;
export type PartnerTaxIdType = (typeof PARTNER_TAX_ID_TYPES)[number];

export const PARTNER_LINK_STATUSES = ['pending', 'approved', 'rejected', 'inactive'] as const;
export type PartnerLinkStatus = (typeof PARTNER_LINK_STATUSES)[number];

/** Áreas que bloqueiam, cada uma com a sua permissão (partner.restriction.<área>). */
export const RESTRICTION_AREAS = ['commercial', 'financial', 'legal'] as const;
export type RestrictionArea = (typeof RESTRICTION_AREAS)[number];

/** Operações que podem receber liberação excepcional de um bloqueio (cresce com os módulos). */
export const WAIVABLE_OPERATIONS = ['purchasing.request'] as const;

export function partnerNumber(n: number): string {
  return `PAR-${String(n).padStart(4, '0')}`;
}

// ----- Documentos -----

const LEGAL_SUFFIXES = new Set([
  'ltda',
  'ltd',
  'me',
  'mei',
  'epp',
  'eireli',
  'sa',
  'inc',
  'llc',
  'corp',
  'co',
  'gmbh',
  'sarl',
  'srl',
  'bv',
  'plc',
  'limited',
  'company',
  'cia',
  'the',
  'de',
  'da',
  'do',
  'dos',
  'das',
]);

/** Nome para achar parecidos: minúsculas, sem acentos, sem pontuação e sem sufixos societários. */
export function partnerNameKey(name: string): string {
  const plain = name
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const kept = plain.split(' ').filter((w) => w && !LEGAL_SUFFIXES.has(w));
  return (kept.length > 0 ? kept.join(' ') : plain).slice(0, 300) || '-';
}

// Códigos que o Intl conhece, mas não são países (União Europeia, ONU, regiões e testes).
const NOT_COUNTRIES = new Set(['EU', 'EZ', 'UN', 'QO', 'XA', 'XB', 'ZZ']);

/** País no padrão ISO 3166 (duas letras), conferido com a lista de regiões do próprio JavaScript. */
export function isKnownCountry(code: string): boolean {
  if (!/^[A-Z]{2}$/.test(code) || NOT_COUNTRIES.has(code)) return false;
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' }).of(code);
    return !!name && name !== code;
  } catch {
    return false;
  }
}

// ----- Entradas -----

const text = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const optionalEmail = optionalText(254).refine(
  (v) => v == null || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v),
  'E-mail inválido',
);
const country = countryCode.refine(isKnownCountry, 'País inválido');
const registration = optionalText(30)
  .transform((v) => (v == null ? v : v.toUpperCase()))
  .refine(
    (v) => v == null || /^[0-9A-Z./ -]+$/.test(v),
    'Use números, letras, ponto, barra ou hífen',
  );

/** Quem é o parceiro: o que decide se ele já existe no grupo. */
export const partnerIdentity = z
  .object({
    personType: z.enum(PARTNER_PERSON_TYPES),
    country,
    taxIdType: z.enum(PARTNER_TAX_ID_TYPES),
    taxId: optionalText(60).transform((v) => (v == null ? null : normalizeTaxId(v))),
    /**
     * Vendedor de marketplace ou loja física (R1; migração 0202): sem documento, mesmo no Brasil,
     * com o link da loja no identificador alternativo. A identificação fiscal vem depois, com a nota.
     */
    marketplaceSeller: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    const issue = (message: string, path = 'taxId') =>
      ctx.addIssue({ code: 'custom', message, path: [path] });
    if (v.marketplaceSeller && v.taxIdType !== 'none') {
      issue('Vendedor de marketplace só sem documento', 'marketplaceSeller');
    }
    if (
      v.country === 'BR' &&
      v.taxIdType !== 'cpf' &&
      v.taxIdType !== 'cnpj' &&
      !(v.marketplaceSeller && v.taxIdType === 'none')
    ) {
      issue('Parceiro do Brasil precisa de CPF ou CNPJ', 'taxIdType');
    }
    if ((v.taxIdType === 'cpf' || v.taxIdType === 'cnpj') && v.country !== 'BR') {
      issue('CPF e CNPJ só para parceiros do Brasil', 'taxIdType');
    }
    if (v.taxIdType === 'cpf' && v.personType !== 'individual') {
      issue('CPF é só para pessoa física', 'taxIdType');
    }
    if (v.taxIdType === 'cnpj' && v.personType !== 'company') {
      issue('CNPJ é só para pessoa jurídica', 'taxIdType');
    }
    if (v.taxIdType === 'none') {
      if (v.taxId) issue('Sem identificação fiscal: deixe o documento em branco');
      return;
    }
    if (!v.taxId) return issue('Informe o documento');
    if (v.taxIdType === 'cpf' && !isValidCpf(v.taxId)) issue('CPF inválido');
    if (v.taxIdType === 'cnpj' && !isValidCnpj(v.taxId)) issue('CNPJ inválido');
    if (v.taxIdType === 'foreign' && v.taxId.length > 40) issue('Identificação muito longa');
  });
export type PartnerIdentity = z.infer<typeof partnerIdentity>;

export const partnerDuplicateCheckInput = z.object({
  identity: partnerIdentity,
  legalName: optionalText(300),
});

const partnerDataShape = {
  legalName: text(300),
  tradeName: optionalText(300),
  addressStreet: optionalText(200),
  addressNumber: optionalText(20),
  addressComplement: optionalText(100),
  addressDistrict: optionalText(100),
  addressCity: optionalText(100),
  addressRegion: optionalText(100),
  addressPostalCode: optionalText(20),
  email: optionalEmail,
  phone: optionalText(40),
  website: optionalText(300),
  notes: optionalText(4000),
  /** Categoria do parceiro (C3a.3): decide quais documentos são exigidos. */
  categoryId: z.preprocess((v) => (v === '' ? null : v), uuid.nullable()).optional(),
  /** Inscrição estadual e municipal: só no Brasil ("ISENTO" quando não houver). */
  stateRegistration: registration,
  municipalRegistration: registration,
};

/** Dados que podem ser alterados depois (a identidade, não: ela define se o parceiro é o mesmo). */
export const partnerDataInput = z.object(partnerDataShape);
export type PartnerDataInput = z.infer<typeof partnerDataInput>;

/**
 * Regras que dependem da origem (ADR 0068): no Brasil, CEP com 8 números e UF da lista; fora
 * dele, sem inscrição estadual nem municipal. Devolve os problemas (vazio = tudo certo).
 */
export function partnerDataIssues(
  country: string,
  v: Pick<
    PartnerDataInput,
    'addressPostalCode' | 'addressRegion' | 'stateRegistration' | 'municipalRegistration'
  >,
): { path: string; message: string }[] {
  const issues: { path: string; message: string }[] = [];
  if (country === 'BR') {
    if (v.addressPostalCode && !cleanCep(v.addressPostalCode)) {
      issues.push({ path: 'addressPostalCode', message: 'CEP com 8 números' });
    }
    if (v.addressRegion && !(BR_STATES as readonly string[]).includes(v.addressRegion)) {
      issues.push({ path: 'addressRegion', message: 'Escolha o estado (UF)' });
    }
  } else {
    if (v.stateRegistration) {
      issues.push({ path: 'stateRegistration', message: 'Inscrição estadual só no Brasil' });
    }
    if (v.municipalRegistration) {
      issues.push({ path: 'municipalRegistration', message: 'Inscrição municipal só no Brasil' });
    }
  }
  return issues;
}

export const partnerCreateInput = z
  .object({
    identity: partnerIdentity,
    ...partnerDataShape,
    legalEntityId: uuid,
    /** Estrangeiro sem identificação fiscal: por que não há, e outro identificador. */
    taxIdExceptionReason: optionalText(1000),
    altIdentifier: optionalText(200),
    /** Há nome igual no mesmo país: por que não é o mesmo parceiro. */
    duplicateCheckNote: optionalText(1000),
    /** CNPJ que não está ativo (ou não existe) na Receita: por que cadastrar mesmo assim. */
    registryStatusNote: optionalText(1000),
  })
  .superRefine((v, ctx) => {
    if (v.identity.taxIdType !== 'none') return;
    if (!v.taxIdExceptionReason || v.taxIdExceptionReason.length < 10) {
      ctx.addIssue({
        code: 'custom',
        message: 'Explique por que não há identificação fiscal (pelo menos 10 caracteres)',
        path: ['taxIdExceptionReason'],
      });
    }
    if (!v.altIdentifier) {
      ctx.addIssue({
        code: 'custom',
        message: 'Informe outro identificador (registro comercial, site ou domínio de e-mail)',
        path: ['altIdentifier'],
      });
    }
  });
export type PartnerCreateInput = z.infer<typeof partnerCreateInput>;

export const partnerContactInput = z.object({
  name: text(200),
  role: optionalText(100),
  email: optionalEmail,
  phone: optionalText(40),
  isPrimary: z.boolean().default(false),
  notes: optionalText(1000),
});
export type PartnerContactInput = z.infer<typeof partnerContactInput>;

export const partnerLinkInput = z.object({ legalEntityId: uuid });

/** Homologar, recusar, inativar e reativar: sempre com um comentário. */
export const partnerDecisionInput = z.object({ comment: text(1000) });

export const restrictionInput = z
  .object({
    area: z.enum(RESTRICTION_AREAS),
    /** Vazio = no grupo inteiro. */
    legalEntityId: uuid.nullable().default(null),
    blocksContracting: z.boolean(),
    blocksPayments: z.boolean(),
    reason: text(1000),
  })
  .refine((v) => v.blocksContracting || v.blocksPayments, {
    message: 'Escolha o que o bloqueio impede',
    path: ['blocksContracting'],
  })
  .refine((v) => v.area !== 'commercial' || !v.blocksPayments, {
    message: 'O bloqueio comercial impede só novas contratações',
    path: ['blocksPayments'],
  });
export type RestrictionInput = z.infer<typeof restrictionInput>;

export const restrictionLiftInput = z.object({ justification: text(1000) });

export const restrictionWaiverInput = z.object({
  operationType: z.enum(WAIVABLE_OPERATIONS),
  operationId: uuid,
  justification: text(1000).refine((v) => v.length >= 10, 'Explique em pelo menos 10 caracteres'),
});

export const externalReferenceInput = z.object({
  system: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z][a-z0-9_]{1,29}$/, 'Use letras minúsculas, números ou _ (ex.: netsuite)'),
  externalCode: text(100),
  notes: optionalText(500),
});

// ----- Situação e aptidão (calculadas, nunca digitadas) -----

export interface SituationRestriction {
  id: string;
  area: RestrictionArea;
  legalEntityId: string | null;
  blocksContracting: boolean;
  blocksPayments: boolean;
}

export interface SituationInput {
  inactivatedAt: Date | string | null;
  /** Vendedor de marketplace (R1): só faz diferença no contexto de aptidão 'marketplace'. */
  marketplaceSeller?: boolean;
  links: readonly { legalEntityId: string; status: PartnerLinkStatus }[];
  /** Só os bloqueios ativos. */
  restrictions: readonly SituationRestriction[];
}

export type PartnerGeneralStatus = 'inactive' | 'blocked' | 'pending' | 'active';

export interface GeneralStatusInput {
  inactivatedAt: Date | string | null;
  /** Tem vínculo homologado em alguma empresa do grupo (mesmo uma que a pessoa não vê). */
  hasApprovedLink: boolean;
  /** Bloqueios ativos que a pessoa vê (os do grupo inteiro aparecem para todos). */
  restrictions: readonly SituationRestriction[];
  /** Tem bloqueio ativo em alguma empresa (mesmo uma que a pessoa não vê). */
  hasEntityRestrictions: boolean;
}

/** Situação no grupo: inativo, bloqueado (bloqueio no grupo), em homologação ou ativo. */
export function partnerGeneralStatus(p: GeneralStatusInput): {
  status: PartnerGeneralStatus;
  /** Áreas com bloqueio ativo no grupo inteiro. */
  blockedAreas: RestrictionArea[];
  /** Há bloqueio em alguma empresa (o parceiro segue "ativo com restrições"). */
  hasEntityRestrictions: boolean;
} {
  const groupAreas = new Set(
    p.restrictions.filter((r) => r.legalEntityId == null).map((r) => r.area),
  );
  const blockedAreas = RESTRICTION_AREAS.filter((a) => groupAreas.has(a));
  let status: PartnerGeneralStatus;
  if (p.inactivatedAt) status = 'inactive';
  else if (blockedAreas.length > 0) status = 'blocked';
  else if (!p.hasApprovedLink) status = 'pending';
  else status = 'active';
  return { status, blockedAreas, hasEntityRestrictions: p.hasEntityRestrictions };
}

/**
 * Contexto da aptidão (o mesmo de partner.contracting_apt): 'marketplace' = compra em marketplace
 * ou loja física, em que o vendedor marcado dispensa a homologação e os documentos obrigatórios.
 */
export const CONTRACTING_CONTEXTS = ['marketplace'] as const;
export type ContractingContext = (typeof CONTRACTING_CONTEXTS)[number];

export type ContractingBlocker =
  'inactive' | 'not_linked' | 'not_approved' | 'restricted' | 'documents';

/**
 * Pode contratar com o parceiro nesta empresa? Bloqueios que impedem contratação (no grupo ou
 * na empresa) contam, a menos que a operação tenha liberação excepcional. A regularidade
 * documental (C3a.3) também conta: faltando documento obrigatório válido na empresa, não contrata.
 * Pagamento é outra regra (C3a.2): bloqueio comercial e inativação não impedem pagar o que já
 * foi contratado. No contexto 'marketplace', o vendedor marcado dispensa a homologação e os
 * documentos; ativo e sem bloqueio continuam exigidos (mesma regra de partner.contracting_apt).
 */
export function contractingAptitude(
  p: SituationInput,
  legalEntityId: string,
  waivedRestrictionIds: ReadonlySet<string> = new Set(),
  documentsRegular = true,
  context: ContractingContext | null = null,
): { apt: boolean; blockers: ContractingBlocker[]; restrictionIds: string[] } {
  const blockers: ContractingBlocker[] = [];
  const marketplace = context === 'marketplace' && !!p.marketplaceSeller;
  if (p.inactivatedAt) blockers.push('inactive');
  const link = p.links.find((l) => l.legalEntityId === legalEntityId);
  // Vendedor de marketplace: homologação dispensada.
  if (!marketplace) {
    if (!link) blockers.push('not_linked');
    else if (link.status !== 'approved') blockers.push('not_approved');
  }
  const restrictionIds = p.restrictions
    .filter(
      (r) =>
        r.blocksContracting &&
        (r.legalEntityId == null || r.legalEntityId === legalEntityId) &&
        !waivedRestrictionIds.has(r.id),
    )
    .map((r) => r.id);
  if (restrictionIds.length > 0) blockers.push('restricted');
  if (!documentsRegular && !marketplace) blockers.push('documents');
  return { apt: blockers.length === 0, blockers, restrictionIds };
}

/** Bloqueios ativos que impedem pagamentos nesta empresa (a aptidão completa vem no C3a.2). */
export function paymentRestrictions(
  p: Pick<SituationInput, 'restrictions'>,
  legalEntityId: string,
): string[] {
  return p.restrictions
    .filter(
      (r) => r.blocksPayments && (r.legalEntityId == null || r.legalEntityId === legalEntityId),
    )
    .map((r) => r.id);
}
